import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ClientService from "../../services/ClientService";
import { Toast } from "primereact/toast";
import RegisterUserService from "../../../Admin/services/RegisterUserService";
import ClientIntegrationPanel from "./ClientIntegrationPanel";
import AddSubscription from "../Subscription/AddSubscription";
import SubscriptionService from "../../services/SubscriptionService";
import WorkspaceSettingsService from "../../../Admin/services/WorkspaceSettingsService";
import ClientSchedulerSettingsPanel from "../../../Admin/components/Settings/ClientSchedulerSettingsPanel";
import {
  SaPageShell,
  SaPageHeader,
  SaTabs,
  SaCard,
  SaPrimaryButton,
  SaGhostButton,
  SaFormActions,
  SaField,
  SaSectionTitle,
  saInputSx,
  SA,
} from "../shared/SuperAdminUi";
import { CLIENT_MODULES } from "../../../../shared/constants/clientModules";

function parseModuleFlags(raw) {
  const base = Object.fromEntries(CLIENT_MODULES.map((m) => [m.key, true]));
  if (raw == null || raw === "") return base;
  let o = raw;
  if (typeof raw === "string") {
    try {
      o = JSON.parse(raw);
    } catch {
      return base;
    }
  }
  if (typeof o !== "object" || o === null) return base;
  CLIENT_MODULES.forEach((m) => {
    if (typeof o[m.key] === "boolean") base[m.key] = o[m.key];
  });
  return base;
}

const ROLES = [
  { id: 1, name: "SuperAdmin" },
  { id: 2, name: "Admin" },
  { id: 4, name: "Employee" },
];

function EditClient() {
  const toast = useRef(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);

  const [form, setForm] = useState({
    agencyName: "",
    email: "",
    phoneNumber: "",
    streetAddress: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    gstNumber: "",
    agencyLogo: null,
    license: "",
    registrationNumber: "",
    leadCodePrefix: "",
    id,
  });

  const [formError, setFormError] = useState({});
  const [activeTab, setActiveTab] = useState("edit");
  const [loading, setLoading] = useState(true);
  const [moduleFlags, setModuleFlags] = useState(() =>
    Object.fromEntries(CLIENT_MODULES.map((m) => [m.key, true]))
  );
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [fence, setFence] = useState({
    officeLatitude: "",
    officeLongitude: "",
    radiusMeters: "",
  });
  const [fenceLoading, setFenceLoading] = useState(false);
  const [fenceSaving, setFenceSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchClient = async () => {
      try {
        const data = await ClientService.getClientById(id);
        if (!cancelled) {
          const {
            moduleAccessJson,
            ModuleAccessJson,
            leadCodePrefix: lpCamel,
            LeadCodePrefix: lpPascal,
            ...profile
          } = data ?? {};
          const jsonRaw =
            moduleAccessJson !== undefined && moduleAccessJson !== null
              ? moduleAccessJson
              : ModuleAccessJson;
          const leadCodePrefix = lpCamel ?? lpPascal ?? "";
          setForm((prev) => ({ ...prev, ...profile, leadCodePrefix, id }));
          setModuleFlags(parseModuleFlags(jsonRaw));
        }
      } catch (error) {
        console.error("Error fetching client:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load client.",
          life: 4000,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchClient();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const fetchSubscriptionStatus = async () => {
      try {
        const status = await SubscriptionService.getClientSubscriptionStatus(id);
        if (!cancelled) setSubscriptionStatus(status);
      } catch (error) {
        console.error("Error fetching subscription status:", error);
      }
    };
    fetchSubscriptionStatus();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const fetchUsers = async () => {
      try {
        const list = await RegisterUserService.getUsersByClientId(id);
        if (!cancelled) setUsers(Array.isArray(list) ? list : list?.$values ?? []);
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch users.",
          life: 4000,
        });
      }
    };
    fetchUsers();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const loadFence = useCallback(async () => {
    if (!id) return;
    setFenceLoading(true);
    try {
      const data = await WorkspaceSettingsService.getAttendanceGeoFence(id);
      setFence({
        officeLatitude: data?.officeLatitude != null ? String(data.officeLatitude) : "",
        officeLongitude: data?.officeLongitude != null ? String(data.officeLongitude) : "",
        radiusMeters:
          data?.radiusMeters != null && data.radiusMeters > 0 ? String(data.radiusMeters) : "",
      });
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Could not load attendance area settings.",
        life: 4000,
      });
    } finally {
      setFenceLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (activeTab === "attendance") loadFence();
  }, [activeTab, loadFence]);

  const saveFence = async () => {
    const lat = fence.officeLatitude.trim() === "" ? null : Number(fence.officeLatitude);
    const lng = fence.officeLongitude.trim() === "" ? null : Number(fence.officeLongitude);
    const r = fence.radiusMeters.trim() === "" ? null : parseInt(fence.radiusMeters, 10);
    if (lat != null && (Number.isNaN(lat) || lat < -90 || lat > 90)) {
      toast.current?.show({ severity: "warn", summary: "Validation", detail: "Latitude must be between -90 and 90.", life: 3500 });
      return;
    }
    if (lng != null && (Number.isNaN(lng) || lng < -180 || lng > 180)) {
      toast.current?.show({ severity: "warn", summary: "Validation", detail: "Longitude must be between -180 and 180.", life: 3500 });
      return;
    }
    if (r != null && (Number.isNaN(r) || r <= 0)) {
      toast.current?.show({ severity: "warn", summary: "Validation", detail: "Radius must be a positive number, or leave empty to disable.", life: 3500 });
      return;
    }
    setFenceSaving(true);
    try {
      await WorkspaceSettingsService.updateAttendanceGeoFence(id, {
        officeLatitude: lat,
        officeLongitude: lng,
        radiusMeters: r,
      });
      toast.current?.show({
        severity: "success",
        summary: "Saved",
        detail: "Attendance area saved. Employees must check in within this radius.",
        life: 3500,
      });
      loadFence();
    } catch {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Could not save attendance area.", life: 4000 });
    } finally {
      setFenceSaving(false);
    }
  };

  const disableFence = async () => {
    setFence({ officeLatitude: "", officeLongitude: "", radiusMeters: "" });
    setFenceSaving(true);
    try {
      await WorkspaceSettingsService.updateAttendanceGeoFence(id, {
        officeLatitude: null,
        officeLongitude: null,
        radiusMeters: null,
      });
      toast.current?.show({ severity: "success", summary: "Disabled", detail: "Attendance geo rule disabled.", life: 3000 });
      loadFence();
    } catch {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Could not disable geo rule.", life: 4000 });
    } finally {
      setFenceSaving(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.current?.show({ severity: "warn", summary: "Unavailable", detail: "Geolocation is not supported on this device.", life: 3500 });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setFence((f) => ({
          ...f,
          officeLatitude: String(p.coords.latitude.toFixed(7)),
          officeLongitude: String(p.coords.longitude.toFixed(7)),
        }));
        toast.current?.show({ severity: "info", summary: "Location", detail: "Coordinates filled from this device.", life: 2500 });
      },
      () => toast.current?.show({ severity: "error", summary: "Error", detail: "Could not read GPS.", life: 3500 }),
    );
  };

  const changeHandler = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const errors = {};
    if (!form.agencyName.trim()) errors.agencyName = "Please enter the agency name.";
    setFormError(errors);
    if (Object.keys(errors).length > 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Please fix the highlighted fields.",
        life: 3500,
      });
    }
    return Object.keys(errors).length === 0;
  };

  const onClientSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    persistClient({ navigateAway: true });
  };

  const buildFormData = () => {
    const fd = new FormData();
    Object.keys(form).forEach((key) => {
      if (key === "moduleAccessJson" || key === "ModuleAccessJson") return;
      fd.append(key, form[key] ?? "");
    });
    fd.append("moduleAccessJson", JSON.stringify(moduleFlags));
    return fd;
  };

  const persistClient = async ({ navigateAway = false } = {}) => {
    try {
      await ClientService.updateClient(id, buildFormData());
      toast.current?.show({
        severity: "success",
        summary: "Saved",
        detail: navigateAway ? "Client updated successfully." : "Module access saved.",
        life: 2500,
      });
      if (navigateAway) setTimeout(() => navigate("/client"), 800);
    } catch (error) {
      console.error("Error updating client:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update client.",
        life: 4000,
      });
    }
  };

  const toggleModule = (key) => {
    setModuleFlags((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const agencyLogo = reader.result.split(",")[1];
      setForm((prev) => ({ ...prev, agencyLogo }));
      setFormError((prev) => ({ ...prev, agencyLogo: "" }));
    };
  };

  const deleteUser = async (userId) => {
    try {
      await RegisterUserService.deleteUser(userId);
      toast.current?.show({
        severity: "success",
        summary: "Removed",
        detail: "User deleted.",
        life: 2500,
      });
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (e) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Could not delete user.",
        life: 3500,
      });
      console.error(e);
    }
  };

  const tabs = [
    { key: "edit", label: "Details", icon: "fas fa-id-card" },
    { key: "modules", label: "Modules", icon: "fas fa-toggle-on" },
    { key: "attendance", label: "Attendance area", icon: "fas fa-map-marker-alt" },
    { key: "scheduler", label: "Lead automation", icon: "fas fa-clock" },
    { key: "integration", label: "Integration", icon: "fas fa-plug" },
    { key: "users", label: "Users", icon: "fas fa-users" },
    { key: "services", label: "Services", icon: "fas fa-briefcase" },
    { key: "subscription", label: "Subscription", icon: "fas fa-receipt" },
  ];

  const grid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: "0 20px",
  };

  const enabledModuleCount = CLIENT_MODULES.filter((m) => moduleFlags[m.key]).length;

  const setAllModules = (enabled) => {
    setModuleFlags(Object.fromEntries(CLIENT_MODULES.map((m) => [m.key, enabled])));
  };

  return (
    <SaPageShell>
      <Toast ref={toast} />
      <SaPageHeader
        title={loading ? "Edit client" : form.agencyName || "Edit client"}
        subtitle={`Client ID ${id}. Manage profile, modules, attendance area, lead automation, integrations, users, and billing.`}
        actions={
          <>
            <SaGhostButton onClick={() => navigate("/client")}>
              <i className="fas fa-arrow-left" style={{ marginRight: "8px" }} />
              All clients
            </SaGhostButton>
          </>
        }
      />

      <SaTabs tabs={tabs} activeKey={activeTab} onChange={setActiveTab} />

      {subscriptionStatus && !subscriptionStatus.hasActiveSubscription ? (
        <div
          style={{
            margin: "16px 0 20px",
            padding: "14px 18px",
            borderRadius: SA.radiusMd,
            border: "1px solid #fde68a",
            background: "#fffbeb",
            color: "#92400e",
            fontSize: "13px",
            lineHeight: 1.55,
          }}
        >
          <strong style={{ display: "block", marginBottom: "4px" }}>
            <i className="fas fa-exclamation-triangle" style={{ marginRight: "8px" }} />
            No active subscription
          </strong>
          This client has{" "}
          {subscriptionStatus.totalCount > 0
            ? `${subscriptionStatus.totalCount} past subscription(s), but none are active today.`
            : "no subscription on record."}{" "}
          Tenant users cannot access modules or API until you add an active subscription under the{" "}
          <strong>Subscription</strong> tab.
        </div>
      ) : null}

      {subscriptionStatus?.hasActiveSubscription ? (
        <div
          style={{
            margin: "16px 0 20px",
            padding: "12px 18px",
            borderRadius: SA.radiusMd,
            border: "1px solid #bbf7d0",
            background: "#f0fdf4",
            color: "#166534",
            fontSize: "13px",
          }}
        >
          <i className="fas fa-check-circle" style={{ marginRight: "8px" }} />
          Active subscription:{" "}
          <strong>
            {subscriptionStatus.activeSubscription?.subscriptionPlan?.subscriptionPlanName ?? "Plan"}
          </strong>
          {" · "}
          until{" "}
          {subscriptionStatus.activeSubscription?.endDate
            ? new Date(subscriptionStatus.activeSubscription.endDate).toLocaleDateString()
            : "—"}
        </div>
      ) : null}

      {activeTab === "edit" && (
        <form onSubmit={onClientSubmit}>
          <SaCard>
            <SaSectionTitle iconClass="fas fa-building">Agency profile</SaSectionTitle>
            <div style={grid}>
              <SaField label="Agency name *">
                <input
                  type="text"
                  name="agencyName"
                  value={form.agencyName}
                  onChange={changeHandler}
                  style={{
                    ...saInputSx,
                    borderColor: formError.agencyName ? SA.danger : SA.border,
                  }}
                />
                {formError.agencyName ? (
                  <p style={{ margin: "6px 0 0", fontSize: "12px", color: SA.danger }}>{formError.agencyName}</p>
                ) : null}
              </SaField>
              <SaField label="Email">
                <input type="email" name="email" value={form.email} onChange={changeHandler} style={saInputSx} />
              </SaField>
              <SaField label="Phone">
                <input type="text" name="phoneNumber" value={form.phoneNumber} onChange={changeHandler} style={saInputSx} />
              </SaField>
              <SaField label="Street address">
                <input type="text" name="streetAddress" value={form.streetAddress} onChange={changeHandler} style={saInputSx} />
              </SaField>
              <SaField label="City">
                <input type="text" name="city" value={form.city} onChange={changeHandler} style={saInputSx} />
              </SaField>
              <SaField label="State">
                <input type="text" name="state" value={form.state} onChange={changeHandler} style={saInputSx} />
              </SaField>
              <SaField label="Postal code">
                <input type="text" name="postalCode" value={form.postalCode} onChange={changeHandler} style={saInputSx} />
              </SaField>
              <SaField label="Country">
                <input type="text" name="country" value={form.country} onChange={changeHandler} style={saInputSx} />
              </SaField>
              <SaField label="GST number">
                <input type="text" name="gstNumber" value={form.gstNumber} onChange={changeHandler} style={saInputSx} />
              </SaField>
              <SaField label="Agency logo">
                <input type="file" accept="image/*" onChange={onFileChange} style={{ fontSize: "13px", color: SA.muted }} />
              </SaField>
              <SaField label="License">
                <input type="text" name="license" value={form.license} onChange={changeHandler} style={saInputSx} />
              </SaField>
              <SaField label="Registration number">
                <input type="text" name="registrationNumber" value={form.registrationNumber} onChange={changeHandler} style={saInputSx} />
              </SaField>
              <SaField label="Lead code prefix" hint="Optional. Alphanumeric; leads use PREFIX-DT5W-id. Blank = DT5W-id only.">
                <input
                  type="text"
                  name="leadCodePrefix"
                  value={form.leadCodePrefix}
                  onChange={changeHandler}
                  placeholder="e.g. ACME"
                  maxLength={32}
                  style={saInputSx}
                />
              </SaField>
            </div>
            <SaFormActions>
              <SaPrimaryButton type="submit" iconClass="fas fa-save">
                Save changes
              </SaPrimaryButton>
            </SaFormActions>
          </SaCard>
        </form>
      )}

      {activeTab === "modules" && (
        <SaCard>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <div style={{ flex: "1 1 240px" }}>
              <SaSectionTitle iconClass="fas fa-toggle-on">Module access</SaSectionTitle>
              <p style={{ margin: "4px 0 0", fontSize: "13px", color: SA.muted, lineHeight: 1.5, maxWidth: 560 }}>
                Enable or disable features for this tenant. Requires an active subscription for tenant users.
              </p>
              {subscriptionStatus && !subscriptionStatus.hasActiveSubscription ? (
                <p style={{ margin: "8px 0 0", fontSize: "12px", color: "#b45309" }}>
                  No active subscription — toggles are saved but not effective until billing is active.
                </p>
              ) : null}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  padding: "6px 12px",
                  background: SA.bgSoft,
                  borderRadius: 999,
                  border: `1px solid ${SA.border}`,
                  fontSize: "12px",
                  fontWeight: "700",
                  color: SA.ink,
                }}
              >
                {enabledModuleCount}/{CLIENT_MODULES.length} on
              </span>
              <SaGhostButton type="button" onClick={() => setAllModules(true)} style={{ padding: "7px 12px", fontSize: "12px" }}>
                All on
              </SaGhostButton>
              <SaGhostButton type="button" onClick={() => setAllModules(false)} style={{ padding: "7px 12px", fontSize: "12px" }}>
                All off
              </SaGhostButton>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: "6px",
            }}
          >
            {CLIENT_MODULES.map((m) => {
              const enabled = !!moduleFlags[m.key];
              return (
                <div
                  key={m.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleModule(m.key)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleModule(m.key);
                    }
                  }}
                  title={m.description}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "8px 12px",
                    minHeight: "44px",
                    border: `1px solid ${enabled ? "rgba(94,114,228,0.28)" : SA.border}`,
                    borderRadius: "10px",
                    background: enabled ? "#f8f9fe" : "#fff",
                    cursor: "pointer",
                    transition: "border-color .15s, background .15s",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      background: enabled ? SA.gradientBtn : SA.bgSoft,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <i
                      className={m.icon || "fas fa-cube"}
                      style={{ color: enabled ? "#fff" : SA.muted, fontSize: "13px" }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: "700",
                        fontSize: "13px",
                        color: SA.ink,
                        lineHeight: 1.25,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {m.label}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: SA.muted,
                        lineHeight: 1.3,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {m.description}
                    </div>
                  </div>
                  <div
                    aria-hidden
                    style={{
                      width: "38px",
                      height: "22px",
                      borderRadius: "11px",
                      background: enabled ? SA.primary : "#d1d5db",
                      position: "relative",
                      flexShrink: 0,
                      transition: "background .2s",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: "2px",
                        left: enabled ? "18px" : "2px",
                        width: "18px",
                        height: "18px",
                        borderRadius: "50%",
                        background: "#fff",
                        transition: "left .2s",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <SaFormActions>
            <SaPrimaryButton type="button" iconClass="fas fa-save" onClick={() => persistClient({ navigateAway: false })}>
              Save module access
            </SaPrimaryButton>
          </SaFormActions>
        </SaCard>
      )}

      {activeTab === "attendance" && (
        <SaCard>
          <SaSectionTitle iconClass="fas fa-map-marker-alt">Office geo-fence</SaSectionTitle>
          <p style={{ margin: "0 0 20px", fontSize: "14px", color: SA.muted, lineHeight: 1.6, maxWidth: 640 }}>
            When latitude, longitude, and a radius (meters) are set, employees must share GPS on check-in/check-out
            and must be within that distance of the office point.
          </p>

          {fenceLoading ? (
            <p style={{ color: SA.muted, textAlign: "center", padding: "32px 0" }}>
              <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} />
              Loading attendance settings…
            </p>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: "16px",
                  marginBottom: "20px",
                }}
              >
                <SaField label="Office latitude (WGS84)">
                  <input
                    type="text"
                    placeholder="e.g. 28.6139"
                    value={fence.officeLatitude}
                    onChange={(e) => setFence((f) => ({ ...f, officeLatitude: e.target.value }))}
                    style={saInputSx}
                  />
                </SaField>
                <SaField label="Office longitude">
                  <input
                    type="text"
                    placeholder="e.g. 77.2090"
                    value={fence.officeLongitude}
                    onChange={(e) => setFence((f) => ({ ...f, officeLongitude: e.target.value }))}
                    style={saInputSx}
                  />
                </SaField>
                <SaField label="Radius (meters)" hint="Leave empty to disable geo-fence.">
                  <input
                    type="text"
                    placeholder="e.g. 200"
                    value={fence.radiusMeters}
                    onChange={(e) => setFence((f) => ({ ...f, radiusMeters: e.target.value }))}
                    style={saInputSx}
                  />
                </SaField>
              </div>

              <SaFormActions bordered>
                <SaGhostButton type="button" onClick={useMyLocation} disabled={fenceSaving}>
                  <i className="fas fa-crosshairs" style={{ marginRight: 8 }} />
                  Use my location
                </SaGhostButton>
                <SaGhostButton type="button" onClick={disableFence} disabled={fenceSaving}>
                  Disable geo rule
                </SaGhostButton>
                <SaPrimaryButton type="button" iconClass="fas fa-save" onClick={saveFence} disabled={fenceSaving}>
                  {fenceSaving ? "Saving…" : "Save attendance rule"}
                </SaPrimaryButton>
              </SaFormActions>
            </>
          )}
        </SaCard>
      )}

      {activeTab === "scheduler" && (
        <SaCard>
          <ClientSchedulerSettingsPanel
            clientId={parseInt(id, 10)}
            canEdit
            onMessage={(msg, type) =>
              toast.current?.show({
                severity: type === "error" ? "error" : "success",
                summary: type === "error" ? "Error" : "Saved",
                detail: msg,
                life: 3500,
              })
            }
          />
        </SaCard>
      )}

      {activeTab === "integration" && <ClientIntegrationPanel clientId={id} />}

      {activeTab === "users" && (
        <SaCard noPadding>
          <div style={{ padding: "20px 24px", display: "flex", justifyContent: "flex-end", borderBottom: `1px solid ${SA.border}` }}>
            <SaPrimaryButton
              iconClass="fas fa-user-plus"
              onClick={() => navigate("/addclientuser", { state: { clientId: id } })}
            >
              Add user
            </SaPrimaryButton>
          </div>
          {users.length === 0 ? (
            <div style={{ padding: "40px 24px", textAlign: "center", color: SA.muted }}>
              No users linked to this client yet.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="table mb-0" style={{ width: "100%" }}>
                <thead style={{ background: SA.bgSoft }}>
                  <tr>
                    {["Name", "Email", "Phone", "Address", "Role", "Status", "Actions"].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: "12px 16px",
                          fontSize: "11px",
                          fontWeight: "800",
                          color: SA.muted,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const roleName = ROLES.find((r) => r.id == user.roleId)?.name ?? "—";
                    return (
                      <tr key={user.id}>
                        <td style={{ padding: "12px 16px", fontSize: "13px", fontWeight: "600", color: SA.ink }}>
                          {user.firstName} {user.lastName}
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: "13px", color: SA.muted }}>{user.email}</td>
                        <td style={{ padding: "12px 16px", fontSize: "13px", color: SA.muted }}>{user.contactNumber}</td>
                        <td style={{ padding: "12px 16px", fontSize: "12px", color: SA.muted, maxWidth: "220px" }}>
                          {`${user.streetAddress || ""}, ${user.city || ""}, ${user.state || ""}`}
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: "13px", color: SA.muted }}>{roleName}</td>
                        <td style={{ padding: "12px 16px", fontSize: "13px" }}>
                          <span
                            style={{
                              padding: "3px 10px",
                              borderRadius: "20px",
                              fontSize: "11px",
                              fontWeight: "700",
                              background: user.isActive ? "#e8f5e9" : "#fdecea",
                              color: user.isActive ? SA.success : SA.danger,
                            }}
                          >
                            {user.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "center" }}>
                          <button
                            type="button"
                            title="Edit user"
                            onClick={() => navigate(`/editclientuser/${user.id}`)}
                            style={{
                              border: `1px solid ${SA.border}`,
                              background: "#fff",
                              borderRadius: "8px",
                              width: "34px",
                              height: "34px",
                              marginRight: "8px",
                              cursor: "pointer",
                              color: SA.primary,
                            }}
                          >
                            <i className="fas fa-pen" />
                          </button>
                          <button
                            type="button"
                            title="Delete user"
                            onClick={() => deleteUser(user.id)}
                            style={{
                              border: `1px solid ${SA.border}`,
                              background: "#fff",
                              borderRadius: "8px",
                              width: "34px",
                              height: "34px",
                              cursor: "pointer",
                              color: SA.danger,
                            }}
                          >
                            <i className="fas fa-trash" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </SaCard>
      )}

      {activeTab === "services" && (
        <SaCard>
          <SaSectionTitle iconClass="fas fa-briefcase">Services</SaSectionTitle>
          <p style={{ margin: 0, fontSize: "14px", color: SA.muted, lineHeight: 1.6 }}>
            Additional managed services for this client can be listed here. Hook this tab to your billing or operations module when ready.
          </p>
        </SaCard>
      )}

      {activeTab === "subscription" && (
        <div style={{ marginTop: "4px" }}>
          <AddSubscription
            activeTab={activeTab}
            ClientId={id}
            onSubscriptionsChanged={async () => {
              try {
                const status = await SubscriptionService.getClientSubscriptionStatus(id);
                setSubscriptionStatus(status);
              } catch (error) {
                console.error("Error refreshing subscription status:", error);
              }
            }}
          />
        </div>
      )}
    </SaPageShell>
  );
}

export default EditClient;
